import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'

import { env } from 'src/config/env'
import { Button } from 'src/shared/components/ui/buttons/button'
import { Form, FormField } from 'src/shared/components/ui/forms/form'
import { InputFormField } from 'src/shared/components/ui/forms/form-fields/input'
import { useConsumerConfirmSignUp, useConsumerResendCode, useConsumerSignUp } from 'src/shared/features/auth/api'
import AuthCodeInput from 'src/shared/features/auth/components/auth-code-input'
import { useToast } from 'src/shared/hooks/use-toast'
import type { ConfirmationCodeFormData, ConsumerSignupFormData } from 'src/shared/schemas/forms/consumer-auth'
import { confirmationCodeSchema, consumerSignupSchema } from 'src/shared/schemas/forms/consumer-auth'
import { useEventStore } from 'src/shared/store/event-store'

import CustomCheckbox from './custom-checkbox'

type ConsumerSignupFormProps = {
  onSuccess: () => void
  onSwitchView: () => void
}

const ConsumerSignupForm = ({ onSuccess, onSwitchView }: ConsumerSignupFormProps) => {
  const [step, setStep] = useState<'signup' | 'verify'>('signup')
  const [email, setEmail] = useState('')
  const [termsAgreed, setTermsAgreed] = useState(false)
  const { toast } = useToast()
  // gclid is used indirectly in useConsumerSignUp and useConsumerConfirmSignUp hooks
  const { gclid, fbclid } = useEventStore() // Get gclid from event store

  // Signup form
  const signupForm = useForm<ConsumerSignupFormData>({
    resolver: zodResolver(consumerSignupSchema),
    mode: 'onSubmit',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
    },
  })

  // Verification code form
  const verificationForm = useForm<ConfirmationCodeFormData>({
    resolver: zodResolver(confirmationCodeSchema),
    mode: 'onSubmit',
    defaultValues: {
      code: '',
    },
  })

  // Hooks for signup process
  const signUp = useConsumerSignUp()
  const confirmSignUp = useConsumerConfirmSignUp()
  const resendCode = useConsumerResendCode()

  // Handle signup form submission
  const handleSignupSubmit = async (data: ConsumerSignupFormData) => {
    try {
      await signUp.mutateAsync({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phoneNumber: data.phoneNumber,
        gclid,
        fbclid,
      })

      // Store email for verification and resending
      setEmail(data.email)

      // Move to verification step
      setStep('verify')

      toast({
        title: 'Verification code sent',
        description: 'Please check your email for the verification code',
        duration: 5000,
      })
    } catch (error) {
      console.error('Signup error:', error)

      // Handle specific errors
      if (error instanceof Error) {
        if (error.name === 'UsernameExistsException') {
          toast({
            title: 'Account already exists',
            description: 'An account with this email already exists. Please log in instead.',
            variant: 'destructive',
          })
          return
        }

        // Show specific error message to help troubleshoot
        toast({
          title: `Error: ${error.name}`,
          description: error.message || 'Failed to create account',
          variant: 'destructive',
        })
      } else {
        // Generic error
        toast({
          title: 'Signup failed',
          description: 'An unexpected error occurred. Please try again.',
          variant: 'destructive',
        })
      }
    }
  }

  // Handle verification code submission
  const handleVerificationSubmit = async (data: ConfirmationCodeFormData) => {
    try {
      if (!data.code || data.code.length < 6) {
        console.error('Verification code is incomplete:', data.code)
        toast({
          title: 'Incomplete code',
          description: 'Please enter the complete 6-digit code',
          variant: 'destructive',
        })
        return
      }

      // Get form data from the signup form
      const formValues = signupForm.getValues()

      await confirmSignUp.mutateAsync({
        email,
        code: data.code,
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        phoneNumber: formValues.phoneNumber,
      })

      onSuccess()
    } catch (error) {
      console.error('Verification error:', error)

      if (error instanceof Error) {
        toast({
          title: `Verification failed: ${error.name}`,
          description: error.message || 'Failed to verify account',
          variant: 'destructive',
        })
      } else {
        toast({
          title: 'Verification failed',
          description: 'An unexpected error occurred. Please try again with a new code.',
          variant: 'destructive',
        })
      }
    }
  }

  // Handle resend code
  const handleResendCode = async () => {
    try {
      await resendCode.mutateAsync(email)

      toast({
        title: 'Code resent',
        description: 'A new verification code has been sent to your email',
      })
    } catch (error) {
      console.error('Resend code error:', error)

      if (error instanceof Error) {
        toast({
          title: `Failed to resend code: ${error.name}`,
          description: error.message || 'Unable to send a new code',
          variant: 'destructive',
        })
      } else {
        toast({
          title: 'Failed to resend code',
          description: 'An unexpected error occurred. Please try again.',
          variant: 'destructive',
        })
      }
    }
  }

  return (
    <div className="relative w-full">
      {step === 'signup' ? (
        <>
          <h2 className="mb-3 text-center text-[28px] font-bold leading-tight text-primary">Create an account</h2>
          <Form {...signupForm}>
            <form onSubmit={signupForm.handleSubmit(handleSignupSubmit)}>
              <InputFormField
                control={signupForm.control}
                name="firstName"
                label="First name"
                placeholder="First name"
                type="text"
                required
                fieldClassName="rounded-[10px]"
              />

              <InputFormField
                control={signupForm.control}
                name="lastName"
                label="Last name"
                placeholder="Last name"
                type="text"
                required
                fieldClassName="rounded-[10px]"
              />

              <InputFormField
                control={signupForm.control}
                name="phoneNumber"
                label="Phone number"
                placeholder="Phone number"
                type="tel"
                required
                fieldClassName="rounded-[10px]"
              />

              <InputFormField
                control={signupForm.control}
                name="email"
                label="Email address"
                placeholder="Email address"
                type="email"
                required
                fieldClassName="rounded-[10px]"
              />
              <div className="mt-4">
                <p className="text-center text-[13px] text-muted">
                  We&apos;ll email you a code to confirm your email.{' '}
                  <a href={env.LINK_TO_PRIVACY_POLICY} className="font-medium text-primary underline">
                    Privacy Policy
                  </a>
                </p>
              </div>
              <Button type="submit" className="mt-4 w-full rounded-full py-4" isLoading={signUp.isPending}>
                {signUp.isPending ? 'Creating Account...' : 'Continue'}
              </Button>
              <div className="mt-3 flex flex-col gap-1 text-center text-sm font-semibold text-primary">
                <p>
                  Already have an account?{' '}
                  <button type="button" onClick={onSwitchView} className="text-primary underline">
                    Log in
                  </button>
                </p>
              </div>
            </form>
          </Form>
        </>
      ) : (
        <Form {...verificationForm}>
          <form onSubmit={verificationForm.handleSubmit(handleVerificationSubmit)} className="space-y-4">
            <h3 className="text-center text-[28px] font-bold leading-tight text-primary">Confirm your email</h3>
            <p className="text-center text-base text-muted">
              You&apos;ve received a code at {email}. Please check your inbox and spam folders to retrieve it.
            </p>

            <FormField
              control={verificationForm.control}
              name="code"
              render={() => (
                <AuthCodeInput
                  inputClassName="mt-1 max-h-[55px] rounded-[10px]"
                  control={verificationForm.control}
                  name="code"
                />
              )}
            />
            <div className="flex items-start gap-2">
              <CustomCheckbox id="terms" checked={termsAgreed} onChange={setTermsAgreed} size="lg" />
              <label htmlFor="terms" className="text-[13px] leading-[150%] text-muted">
                I confirm I am 18+ and consent to: (1) sharing my information with real estate professionals; (2)
                receiving communications from Conrad and these professionals via email, calls, and texts; and (3) the
                collection of my data as per the{' '}
                <a href={env.LINK_TO_PRIVACY_POLICY} className="font-medium text-primary underline">
                  Privacy Policy
                </a>
                . I agree to the{' '}
                <a href={env.LINK_TO_TERMS_OF_USE} className="font-medium text-primary underline">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href={env.LINK_TO_PRIVACY_POLICY} className="font-medium text-primary underline">
                  Privacy Policy
                </a>
                , which will be updated for consumer use. I can manage my preferences in settings.
              </label>
            </div>

            <div className="flex flex-col gap-2">
              <Button
                type="submit"
                className="w-full rounded-full bg-primary py-4 text-white hover:bg-primary/80"
                isLoading={confirmSignUp.isPending}
                disabled={!verificationForm.watch('code') || !termsAgreed}
              >
                {confirmSignUp.isPending ? 'Verifying...' : 'Verify & sign up'}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="h-auto min-h-0 text-sm font-semibold text-primary underline"
                onClick={handleResendCode}
              >
                {resendCode.isPending ? 'Resending...' : 'Resend code'}
              </Button>
            </div>
          </form>
        </Form>
      )}
    </div>
  )
}

export default ConsumerSignupForm
